import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Book,
  Edit,
  FileText,
  Store,
  ShoppingBag,
  Home as HomeIcon,
  Star,
  Cog,
  Upload,
  Mail,
  PenLine,
  HelpCircle,
} from "lucide-react";
import Logo from "../../assets/TheBC.png";
import PortalNavigation from "../shared/PortalNavigation";
import { fetchUserAttributes, signOut } from "@aws-amplify/auth";
import { clearAuthType } from "../../auth/authTypeStorage";
import { readAuthorSetupIdentity, setupRequest } from '../../auth/authorSetup';
import { hasMembershipAccess, validateMembershipStatus } from '../../utils/membershipState';

import { createOrdersApi } from "./ordersApi";
import Blog from "./Blog";
import AddBook from "./AddBook";
import MyMembership from "./MyMembership";
import MyStripeDashboard from "./MyStripeDashboard";
import Home from "./Home";
import MyBooks from "./MyBooks";
import CustomPage from "./CustomPage";
import NewsletterPage from "./NewsletterPage";
import WelcomePage from "./WelcomePage";
import LetsGetStarted from "./LetsGetStarted";
import FoundingRewards from "./FoundingRewards";
import BookDetails from "./BookDetails";
import Orders from "./Orders";
import Settings from "./Settings";

{/*import MembershipModal from "./MembershipModal";*/}

/** —— TABS ——————————————————————————————————— */
const validTabs = [
  "home",
  "getstarted",
  "books",
  "addbook",
  "bookdetails",
  "blog",
  "orders",
  "newsletterpage",
  "settings",
  "welcome",
  "custompage",
];

function normalizeTabParam(val) {
  if (!val) return "";
  const key = String(val).toLowerCase();
  if (key === "letsgetstarted") return "getstarted";
  return validTabs.includes(key) ? key : "";
}

const settingsTabs = ["membership", "stripe", "shipping", "rewards"];


const buildAuthorSiteUrl = (subdomain = "") => {
  const value = String(subdomain || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
};

const extractAuthorSubdomain = (profile = {}) => {
  return (
    profile?.subdomain ||
    profile?.profile?.subdomain ||
    profile?.user?.subdomain ||
    ""
  );
};

const MainPage = () => {
  const [firstName, setFirstName] = useState("");
  {/*const [showMembershipModal, setShowMembershipModal] = useState(false);*/}

  const [membershipActive, setMembershipActive] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [bootChecked, setBootChecked] = useState(false);
  const [membershipError, setMembershipError] = useState('');
  const [membershipRetry, setMembershipRetry] = useState(0);
  const [foundingRewards, setFoundingRewards] = useState(null);
  const [authorSiteUrl, setAuthorSiteUrl] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  /** Initial tab */
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = normalizeTabParam(params.get("tab"));
    if (fromQuery) return fromQuery;

    const stateTab = location.state?.activeTab;
    return validTabs.includes(stateTab) ? stateTab : "home";
  });

  /** Settings sub-tabs */
  const [settingsTab, setSettingsTab] = useState(() => {
    const saved = sessionStorage.getItem("settingsTab");
    return settingsTabs.includes(saved) ? saved : "membership";
  });

  /** Onboarding lock */
  const onboardingLocked = useMemo(() => {
    return bootChecked && (!membershipActive || !onboardingComplete);
  }, [bootChecked, membershipActive, onboardingComplete]);

  /** Load user name */
  useEffect(() => {
    const loadUserName = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const fname = attributes["custom:firstname"] || "Author";
        setFirstName(fname);
      } catch (err) {
        console.error("Failed to fetch user attributes:", err);
        setFirstName("Author");
      }
    };
    loadUserName();
  }, []);

  /** Load author site URL */
  useEffect(() => {
    let isMounted = true;

    const loadAuthorSiteUrl = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const userSub = attributes.sub;
        const attributeSubdomain =
          attributes["custom:subdomain"] ||
          attributes["custom:authorsubdomain"] ||
          attributes["custom:domain"] ||
          "";

        let nextSubdomain = attributeSubdomain;

        if (!nextSubdomain && userSub) {
          const profile = await createOrdersApi().profile();
          nextSubdomain = extractAuthorSubdomain(profile);
        }

        if (isMounted) {
          setAuthorSiteUrl(buildAuthorSiteUrl(nextSubdomain));
        }
      } catch (err) {
        console.error("Failed to load author site URL:", err);
        if (isMounted) {
          setAuthorSiteUrl("");
        }
      }
    };

    loadAuthorSiteUrl();

    return () => {
      isMounted = false;
    };
  }, []);

  /** Membership/onboarding check */
  useEffect(() => {
    let current = true;
    const checkMembershipAndOnboarding = async () => {
      try {
        const identity = await readAuthorSetupIdentity();
        const data = validateMembershipStatus(await setupRequest(
          "https://7n4zqfucx3.execute-api.us-west-2.amazonaws.com/default/checkMembershipStatus",
          identity, { userSub: identity.userSub }
        ));
        if (!current) return;
        setMembershipError('');
        setFoundingRewards(data.foundingRewards || null);
        const onboardingDone = data.onboardingcomplete === true || data.onboardingcomplete === 'true';

        const isMemberActive = hasMembershipAccess(data);
        setMembershipActive(isMemberActive);
        setOnboardingComplete(!!onboardingDone);

        sessionStorage.setItem("custom:membershipactive", isMemberActive ? "true" : "false");
        sessionStorage.setItem("custom:onboardingcomplete", onboardingDone ? "true" : "false");

        {/*const shouldShowModal = !(isMemberActive || onboardingDone === true);
        setShowMembershipModal(shouldShowModal);*/}

        setBootChecked(true);
      } catch (err) {
        if (!current) return;
        setMembershipError('We couldn’t confirm your membership. Retry the check before continuing. Your saved membership information has not been changed.');
        setFoundingRewards(null);
        {/*setShowMembershipModal(true);*/}
        setBootChecked(true);
      }
    };

    checkMembershipAndOnboarding();
    return () => { current = false; };
  }, [membershipRetry]);

  useEffect(() => {
    const refresh = () => setMembershipRetry(value => value + 1);
    window.addEventListener('bookish:author-books-saved', refresh);
    return () => window.removeEventListener('bookish:author-books-saved', refresh);
  }, []);

  useEffect(() => {
    if (!foundingRewards?.enabled || activeTab !== 'settings' || settingsTab !== 'rewards') return undefined;
    const timer = setInterval(() => setMembershipRetry(value => value + 1), 60000);
    return () => clearInterval(timer);
  }, [foundingRewards?.enabled, activeTab, settingsTab]);

  /** Sync tab with URL (and Stripe redirects) */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const payment = params.get("payment");
    const connect = params.get("connect");
    const isReturn =
      payment === "success" ||
      payment === "cancelled" ||
      connect === "done" ||
      connect === "canceled";

    const incomingTab = normalizeTabParam(params.get("tab"));

    let nextTab = activeTab;

    if (incomingTab) nextTab = incomingTab;
    else if (isReturn) nextTab = "getstarted";

    if (onboardingLocked && nextTab !== "settings") nextTab = "getstarted";

    if (nextTab !== activeTab) setActiveTab(nextTab);

    const canonical = new URLSearchParams(location.search);
    canonical.set("tab", nextTab);
    canonical.delete("payment");
    canonical.delete("connect");

    if (canonical.toString() !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: canonical.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, onboardingLocked]);

  /** Helpers */
  const setTabAndUrl = (next, state) => {
    if (!window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) return;
    let normalized = normalizeTabParam(next) || "home";
    if (onboardingLocked && normalized !== "settings") normalized = "getstarted";

    setActiveTab(normalized);
    const params = new URLSearchParams(location.search);
    params.set("tab", normalized);
    params.delete("payment");
    params.delete("connect");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true, state: state ?? location.state }
    );
  };

  const handleTabChange = (tab) => setTabAndUrl(tab);

  const handleSettingsTabChange = (tab) => {
    if (tab === settingsTab || !window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) return;
    setSettingsTab(tab);
    sessionStorage.setItem("settingsTab", tab);
  };

  const handleSignOut = async () => {
    if (!window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) return;
    try {
      await signOut();
      sessionStorage.clear();
      localStorage.removeItem("user");
      clearAuthType();
      navigate("/", { replace: true });
      setTimeout(() => window.location.replace("/"), 0);
    } catch (error) {
      console.error("❌ Sign out error:", error);
    }
  };

  /** Settings panel */
  const SettingsPanel = () => {
    return (
      <div className="space-y-4">
        <div role="group" aria-label="Account settings sections" className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2">
          {settingsTabs.map((tab) => (
            <button
              key={tab}
              aria-pressed={settingsTab === tab}
              onClick={() => handleSettingsTabChange(tab)}
              className={`rounded-md border px-3 py-2 min-h-[44px] text-left text-sm font-medium transition ${
                settingsTab === tab
                  ? "bg-bookish-purple border-bookish-purple text-white"
                  : "border-bookish-purple/20 text-bookish-purpleAccent hover:bg-bookish-purple/10 hover:text-bookish-purple"
              }`}
            >
              {tab === "stripe" && "My Stripe Dashboard"}
              {tab === "membership" && "Membership & Billing"}
              {tab === "shipping" && "Shipping Address"}
              {tab === "rewards" && "Founding Author Rewards"}
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-bookish-purple/15 bg-white p-4">
          {settingsTab === "stripe" && <MyStripeDashboard />}
          {settingsTab === "membership" && <MyMembership />}
          {settingsTab === "rewards" && (foundingRewards ? <FoundingRewards data={foundingRewards} onRefresh={() => setMembershipRetry(value => value + 1)} /> : <p>Founding author reward information is currently unavailable.</p>)}
          {settingsTab === "shipping" && (
            <Settings shouldScrollToShippingAddress={Boolean(location.state?.scrollToShippingAddress)} />
          )}
        </div>
      </div>
    );
  };

  /** Render main content */
  const renderMainContent = () => {
    switch (activeTab) {
      case "getstarted":
        return <LetsGetStarted />;
      case "home":
        return <Home />;
      case "addbook":
        return <AddBook />;
      case "bookdetails":
        return <BookDetails />;
      case "books":
        return (
          <MyBooks
            onEditBook={(book) =>
              setTabAndUrl("bookdetails", {
                ...(location.state || {}),
                book,
                activeTab: "bookdetails",
              })
            }
          />
        );
      case "welcome":
        return <WelcomePage />;
      case "blog":
        return <Blog />;
      case "orders":
        return <Orders />;
      case "custompage":
        return <CustomPage />;
      case "newsletterpage":
        return <NewsletterPage />;
      case "settings":
        return SettingsPanel();
      default:
        return <Home />;
    }
  };

  /** Sidebar menu visibility rules */
  const showLetsGetStartedTab = useMemo(() => !onboardingComplete, [onboardingComplete]);

  /**
   * NAV sections
   */
  const navSections = useMemo(() => {
  return [
    {
      dividerTop: true,
      title: null,
      items: [
        showLetsGetStartedTab && {
          key: "getstarted",
          label: "Let’s Get Started",
          icon: FileText,
          onClick: () => handleTabChange("getstarted"),
          active: activeTab === "getstarted",
        },
        {
          key: "home",
          label: "Home",
          icon: HomeIcon,
          onClick: () => handleTabChange("home"),
          active: activeTab === "home",
          disabled: onboardingLocked,
        },
      ].filter(Boolean),

    },

    {
      items: [
        {
          key: "addbook",
          label: "Add Books",
          icon: Upload,
          onClick: () => handleTabChange("addbook"),
          active: activeTab === "addbook",
          disabled: onboardingLocked,   // 
          },
          {
            key: "books",
            label: "My Books",
            icon: Book,
            onClick: () => handleTabChange("books"),
            active: activeTab === "books",
            disabled: onboardingLocked,
          },
          {
            key: "welcome",
            label: "Welcome Page",
            icon: FileText,
            onClick: () => handleTabChange("welcome"),
            active: activeTab === "welcome",
            disabled: onboardingLocked,
          },
          {
            key: "blog",
            label: "Blog",
            icon: PenLine,
            onClick: () => handleTabChange("blog"),
            active: activeTab === "blog",
            disabled: onboardingLocked,
          },
          {
            key: "orders",
            label: "Orders",
            icon: ShoppingBag,
            onClick: () => handleTabChange("orders"),
            active: activeTab === "orders",
            disabled: onboardingLocked,
          },
          {
            key: "custompage",
            label: "Custom Page",
            icon: Star,
            onClick: () => handleTabChange("custompage"),
            active: activeTab === "custompage",
            disabled: onboardingLocked,
          },
          {
            key: "newsletterpage",
            label: "Newsletter Page",
            icon: Edit,
            onClick: () => handleTabChange("newsletterpage"),
            active: activeTab === "newsletterpage",
            disabled: onboardingLocked,
          },
        ],
      },
      {
        dividerTop: true,
        items: [
          {
            key: "store-link",
            label: "See My Site",
            icon: Store,
            href: authorSiteUrl,
            active: false,
            disabled: onboardingLocked || !authorSiteUrl,
          },
        ],
      },
      {
        title: null,
        dividerTop: true,
        items: [
          {
            key: "mailcenter",
            label: "Mail Center",
            icon: Mail,
            onClick: () => { if (window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) navigate("/authorportal/mailcenter"); },
            active: false,
            disabled: onboardingLocked,
            
          },
          {
            key: "settings",
            label: "Account Settings",
            icon: Cog,
            onClick: () => handleTabChange("settings"),
            active: activeTab === "settings",
            disabled: false,
          },
        ],
      },
    ];
  }, [
    onboardingLocked,
    showLetsGetStartedTab,
    activeTab,
    navigate,
    handleTabChange,
    onboardingComplete,
    authorSiteUrl,
  ]);

  if (!bootChecked) {
  return null; // or a small loading spinner
}

  return (
    <div className="min-h-screen bg-bookish-cream text-bookish-purple">
      {/* FULL-WIDTH TOP BAR (like dev) */}
      <header className="w-full bg-bookish-royal text-bookish-cream">
        <div className="w-full px-3 md:px-6 py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h1 className="text-xl text-bookish-cream font-serif font-bold">
              Welcome back, {firstName}
            </h1>

            <div className="flex items-center gap-4 text-sm md:text-base font-semibold">
              <NavLink to="/market" className="text-bookish-cream hover:text-bookish-cream px-2">
                <span className="block">Bookish Circle Market</span>
              </NavLink>

              <button onClick={handleSignOut} className="text-bookish-cream hover:underline px-2">
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* BODY */}
      <div className="w-full flex flex-col md:flex-row gap-2 px-0 md:px-0 min-w-0">
        <PortalNavigation mobileMenu title="Author Portal" logo={Logo} sections={navSections} footer={<button type="button" onClick={() => { if (window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) navigate('/authorportal/mailcenterpageuc'); }} className="flex min-h-12 items-center gap-2 py-3 text-sm text-bookish-purpleAccent hover:text-bookish-purple hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bookish-purple"><HelpCircle aria-hidden="true" className="h-4 w-4" />Contact Lady Liza</button>} />
        {/* Main Content */}
        <section className="flex-1 min-w-0 space-y-5 mt-4 px-3 md:px-6 pb-6">
          {membershipError && <div className="rounded-xl bg-bookish-cream p-5 text-bookish-royal"><p role="alert">{membershipError}</p><button className="underline min-h-[44px]" onClick={() => setMembershipRetry(value => value + 1)}>Retry membership check</button></div>}
          {(!membershipError || activeTab === 'settings') && renderMainContent()}

          {/*{showMembershipModal && (
            <MembershipModal onClose={() => setShowMembershipModal(false)} />
          )}*/}
        </section>
      </div>
    </div>
  );
};

export default MainPage;
